import React, { useCallback, useMemo, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Button } from "~/components/ui/button";
import {
  SET_CIO_SELECTED_FACETS,
  useCioFacetSelector,
} from "~/hec/context/cio-facet-adapter";
import { filterAndRenameCioFacets } from "~/hec/utils/facetHelpers";
import { isInvalidPriceRange } from "~/hec/utils/price-range";
import { isEmptyObject } from "~/utils/common";

import { FilterTags } from "../filter-tags/filter-tags";
import { FacetOptionsSection } from "./filter-options-section";
import { FilterPriceRangeInput } from "./filter-price-range-input";
import {
  getNewSelectedFilters,
  handlePriceInputChangeHelper,
} from "./helpers.cio.drawerContent";

interface CioFiltersDrawerContentProps {
  attributes?: React.HTMLAttributes<HTMLDivElement>;
  onOpenChange: () => void;
  "data-testid"?: string;
}

export const CioFiltersDrawerContent: React.FC<CioFiltersDrawerContentProps> =
  React.memo(({ attributes, onOpenChange, "data-testid": dataTestId }) => {
    const [priceRange, setPriceRange] = useState<{
      min?: string;
      max?: string;
    }>(() => {
      // Read initial price from URL (SSR-safe: check window exists)
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const min = params.get("pmin") || undefined;
        const max = params.get("pmax") || undefined;
        // A backwards URL range (pmin > pmax, e.g. a hand-edited or stale link)
        // is clamped to pmin = pmax so the drawer opens on a valid range rather
        // than showing an impossible band. The loader clamps the same way for
        // the CIO query (parseHecFilters), so the two stay in sync.
        if (isInvalidPriceRange(min, max)) {
          return { min: max, max };
        }
        return { min, max };
      }
      return { min: undefined, max: undefined };
    });
    const [showMore, setShowMore] = useState<{ [key: string]: boolean }>({});
    const [priceError, setPriceError] = useState<string | null>(null);
    const { cioFacets, totalResults, selectedCioFacets, facetMappingConfig } =
      useCioFacetSelector(context => context?.state);
    const hasSelectedFacets = !isEmptyObject(selectedCioFacets);

    const dispatchSelectedFacets = useCioFacetSelector(
      context => context?.dispatch,
    );

    // Filter + rename facets with the mapping the loader resolved from BM
    // (`Constructor_FacetMapping`), carried on the facet-adapter state — so the
    // drawer shows exactly the facets the CIO query was allowed to filter by.
    const filteredAndRenamedCioFacets = useMemo(
      () => filterAndRenameCioFacets(cioFacets, facetMappingConfig),
      [cioFacets, facetMappingConfig],
    );

    interface CheckboxChangeOption {
      value: string;
      display_name: string;
      count: number;
    }

    const handleCheckboxChange = useCallback(
      (category: string, option: CheckboxChangeOption) => {
        const newSelectedFilters = getNewSelectedFilters(
          selectedCioFacets,
          category,
          option,
        );

        const wouldChange =
          JSON.stringify(selectedCioFacets) !==
          JSON.stringify(newSelectedFilters);

        if (wouldChange) {
          dispatchSelectedFacets({
            type: SET_CIO_SELECTED_FACETS,
            payload: newSelectedFilters,
          });
        }
      },
      [selectedCioFacets, dispatchSelectedFacets],
    );

    const handleShowMoreToggle = useCallback((category: string) => {
      setShowMore(prev => ({
        ...prev,
        [category]: !prev[category],
      }));
    }, []);

    const handlePriceInputChange = useCallback(
      (type: "min" | "max", value: string) => {
        handlePriceInputChangeHelper(
          type,
          value,
          priceRange,
          selectedCioFacets,
          setPriceRange,
          dispatchSelectedFacets,
          setPriceError,
        );
      },
      [priceRange, selectedCioFacets, setPriceRange, dispatchSelectedFacets],
    );

    return (
      <div className="relative flex grow flex-col" {...attributes}>
        {hasSelectedFacets && (
          <FilterTags className="flex border-b p-4 md:hidden" />
        )}

        <div className="flex-1 overflow-y-auto">
          {filteredAndRenamedCioFacets?.map((facet: any) => (
            <Accordion
              type="multiple"
              key={facet.name}
              data-testid={dataTestId || "CioFiltersDrawerContent-Accordion"}
            >
              <AccordionItem
                value={facet.name}
                data-testid={
                  dataTestId || "CioFiltersDrawerContent-AccordionItem"
                }
              >
                <AccordionTrigger
                  arrowDirection="down"
                  className="cursor-pointer p-5 font-grotesque text-sm font-medium uppercase text-blue-03 hover:no-underline"
                  data-testid={
                    dataTestId || "CioFiltersDrawerContent-AccordionTrigger"
                  }
                >
                  {/* Title + selected-count pill grouped so they sit together on
                      the left (the trigger is justify-between, arrow on the
                      right). The pill matches the Filter drawer-trigger badge so
                      it's obvious which sections have active filters. */}
                  <span className="flex items-center gap-2">
                    {facet.display_name}
                    {(selectedCioFacets[facet.name]?.length ?? 0) > 0 && (
                      <span
                        className="rounded-full bg-accent px-1.5 py-0.5 text-xs normal-case text-primary-foreground"
                        data-testid="filter-section-count"
                      >
                        {selectedCioFacets[facet.name].length}
                      </span>
                    )}
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  className="px-5"
                  data-testid={
                    dataTestId || "CioFiltersDrawerContent-AccordionContent"
                  }
                >
                  {facet.name === "price" ? (
                    <div className="flex flex-col items-center gap-2">
                      <FilterPriceRangeInput
                        data-testid={
                          dataTestId || "CioFiltersDrawerContent-PriceRange"
                        }
                        priceRange={priceRange}
                        minOnChange={(e: any) =>
                          handlePriceInputChange("min", e.target.value)
                        }
                        maxOnChange={(e: any) =>
                          handlePriceInputChange("max", e.target.value)
                        }
                      />
                      {priceError && (
                        <p
                          role="alert"
                          className="w-full max-w-96 font-grotesque text-sm text-destructive"
                          data-testid="price-range-error"
                        >
                          {priceError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <FacetOptionsSection
                      facet={facet}
                      showMore={showMore}
                      onShowMoreToggle={handleShowMoreToggle}
                      selectedCioFacets={selectedCioFacets}
                      onCheckboxChange={handleCheckboxChange}
                      dataTestId={dataTestId}
                      data-testid={
                        dataTestId ||
                        "CioFiltersDrawerContent-FacetOptionsSection"
                      }
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}
        </div>
        <div className="sticky bottom-0 flex w-full justify-center border-t bg-[#f5f5f5]">
          <Button
            variant="cta"
            size="5"
            // The `cta` variant sets `font-grotesque` but not a weight, so it
            // inherits the button base's `font-semibold` and renders
            // over-bolded; pin to medium to match production (SFCC-11626).
            className="my-4 cursor-pointer font-medium"
            onClick={onOpenChange}
            data-testid={dataTestId || "CioFiltersDrawerContent-Button"}
          >
            Show Results {totalResults.responseTotal}
          </Button>
        </div>
      </div>
    );
  });

CioFiltersDrawerContent.displayName = "CioFiltersDrawerContent";
